<template>
  <div
    style="width: 100%; height: 100%"
    @mouseover="hover = true"
    @mouseleave="hover = false"
  >
    <control-base
      :config="conf"
      :dataType="dataType"
      :parentName="$options.name"
      @property-selected="addProperty"
      @property-removed="removeProperty"
      @state-selected="addState"
      @state-removed="removeState"
      @loaded="$emit('loaded', $event)"
      ref="base"
    >
      <template v-slot:filters>
        <div class="flex-full">
          <p>Show Robots:</p>
          <input type="checkbox" v-model="conf.showRobots" />
        </div>

        <div class="flex-full">
          <p>Rows per page:</p>
          <select class="browser-default" v-model="conf.rows">
            <option
              v-for="(option, index) in perPage"
              :key="index"
              :value="option"
              :selected="option == conf.rows"
            >
              {{ option === -1 ? "All" : option }}
            </option>
          </select>
        </div>

        <div style="border: 1px solid gray; padding: 5px; margin-top: 5px">
          <p>State Colours</p>
          <div
            class="flex-full"
            v-for="(state, index) in conf.chosenStates"
            :key="index"
          >
            <p class="vertical-center">
              {{ getStateTextByValue(state.state) }}
            </p>
            <colour-picker
              @input="refreshTable"
              :color="state.color"
              v-model="state.color"
            />
          </div>
        </div>

        <div class="flex-full">
          <p class="vertical-center">Table grouping:</p>
          <drop-down
            class="w-full"
            :options="['None', ...conf.agentProperties]"
            :selected="conf.selectedGroup"
            placeholder="None"
            @updateOption="selectGrouping"
            :closeOnOutsideClick="true"
          />
        </div>
      </template>
      <template v-slot:control>
        <DataTableDx
          class="table"
          title="Agents"
          :columns="cols"
          :data="agentData"
          :groupedBy="grouping"
          table-key="agentName"
          @selection-changed="updateSelected"
          :page-size="conf.rows"
          @columns-changed="saveCols"
          :color-dict="conf.chosenStates"
          :contextMenu="addContextMenu"
          selection-mode="single"
          ref="table"
        />
        <span v-if="showButton" class="btn-pos">
          <div v-if="buttonState == 0" class="gray monitor-btn">
            Select an agent to start monitoring
          </div>
          <div
            class="amber monitor-btn"
            v-else-if="buttonState == 1"
            @click="openMonitoring"
          >
            Start monitoring
          </div>
          <div
            class="green monitor-btn"
            v-else-if="buttonState == 2"
            @click="cancelMonitoring"
          >
            Cancel monitoring on: {{ monitoringExtension }}
          </div>
        </span>
        <monitor-agent v-if="showMonitoring" @close="showMonitoring = false" />
        <popup ref="confirm"></popup>
      </template>
    </control-base>
  </div>
</template>

<script>
import { MonitorAgent, DataTableDx } from "@/components";
import ArrayStore from "devextreme/data/array_store";

export default {
  name: "AgentTable",

  components: {
    MonitorAgent,
    DataTableDx,
  },

  props: {
    config: {
      required: true,
      value: Object,
    },

    shouldResize: {
      required: true,
      value: Boolean,
    },
  },

  data() {
    return {
      dataType: "agent",
      showMonitoring: false,
      currentPage: 1,
      perPage: [-1, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50],
      reactiveShouldResize: this.shouldResize,
      hover: false,
      colChange: false,
      timeInStateInterval: null
    };
  },

  destroyed(){
    if(this.timeInStateInterval) clearInterval(this.timeInStateInterval)
  },

  watch: {
    shouldResize() {
      this.reactiveShouldResize = this.shouldResize;
      this.$nextTick(() => this.$emit("resized"));
    },

    configCampaigns() {
      this.colChange = true;
    }
  },

  async mounted() {
    window.addEventListener("agentStateChange", this.updateState); // Used to detect gdmsmonitor logging in
    this.$emit("resized");
    this.registerDependencies();
  },

  computed: {
    agentData() {
      if (Object.keys(this.$store.getters.agentData).length == 0) {
        return new ArrayStore({
          key: "agentName",
          data: [],
        });
      }
      if(this.cols.findIndex(x=> x.field == "timeInCurrentState") != -1) this.updateTimeInStateAsync();
      return new ArrayStore({
        key: "agentName",
        data: this.$store.getters.agentData.filter((val) => {
          var campaign = this.conf.campaignNames.includes(val.campaign);
          var spoof = !val.agentName.startsWith("Spoof");
          var state = this.conf.agentStates.includes(val.agentStateNum);
          var robot = this.conf.showRobots
            ? true
            : !val.agentName.startsWith("Robot");
          var gdmsmonitor = this.conf.showGDMSMonitor
            ? true
            : !val.agentName.startsWith("gdmsmonitor");

          return campaign && spoof && state && robot && gdmsmonitor;
        }),
      });
    },

    cols() {
      var cols = [...this.conf.columns];
      if (cols.findIndex((x) => x.field == "agentName") == -1)
        cols.push({
          label: this.addSpaces("AgentName"),
          field: this.fieldCase("AgentName"),
          visible: false,
          width: 0,
          numeric: false,
          html: false,
        });
      return cols;
    },

    showButton() {
      return (
        (this.hover && this.$store.getters.monitoringAgent.agentName) ||
        this.monitoringState == this.$constants.MonitoringState.Connected ||
        this.monitoringState == this.$constants.MonitoringState.Monitoring
      );
    },

    conf() {
      return this.config;
    },

    configCampaigns() {
      return this.conf.campaignNames;
    },

    layoutLocked() {
      return this.$store.getters.locked;
    },

    monitoringState() {
      return this.$store.getters.monitoringState;
    },

    monitoringAgent() {
      return this.$store.getters.monitoringAgent;
    },

    monitoringExtension() {
      return this.$store.getters.monitoringExtension;
    },

    buttonState() {
      if (
        this.monitoringState == this.$constants.MonitoringState.NotConnected &&
        this.isEmpty(this.monitoringAgent)
      )
        return 0; //Not connected & no agent selected
      if (this.monitoringState == this.$constants.MonitoringState.NotConnected)
        return 1; // Agent selected but not connected
      return 2; //Monitoring an agent
    },

    grouping() {
      if (!this.conf.selectedGroup) return null;
      if (this.conf.selectedGroup == "None") return null;
      else return this.fieldCase(this.conf.selectedGroup);
    },
  },

  methods: {
    updateTimeInStateAsync() {
      if(this.timeInStateInterval) clearInterval(this.timeInStateInterval)
      this.timeInStateInterval = setInterval(() => {
        var count = this.agentData._array.length
        var arr = this.agentData._array
        for (let i = 0; i < count; i++) {
          var key = arr[i].agentName;
          this.agentData.push([{
            type: "update",
            key: key,
            data: {
              ...arr[i],
              timeInCurrentState: this.toHHMMSS(this.now - arr[i].timestampCurrentState)
            }
          }]);
        }
      }, 1000)
    },

    addContextMenu(e) {
      if (e.row.rowType === "data") {
        if (!e.items) e.items = [];
        e.items.push(
          ...[
            {
              text: "Kick Agent",
              onItemClick: () => {
                this.kickAgent(e.row.key)
              },
            },
          ]
        );
      }
    },

    saveCols(data) {
      this.conf.columns = data.map((x) => {
        var index = this.conf.columns.findIndex((z) => z.field == x.field);
        return {
          ...x,
          label: this.conf.columns[index].label,
          color: this.conf.columns[index].color,
        };
      });
      this.$emit("configChange", this.conf);
    },

    addProperty(name) {
      if (name == "AgentState") this.addStateColourProperty();

      this.conf.columns.push({
        label: this.addSpaces(name),
        field: this.fieldCase(name),
        width: 0,
        numeric: false,
        html: false,
      });
      this.$emit("configChange", this.conf);
    },

    selectGrouping(item) {
      this.conf.selectedGroup = item;
      this.$emit("configChange", this.conf);
    },

    removeProperty(name) {
      if (name == "AgentState") this.removeStateColourProperty();

      var index = this.conf.columns.findIndex(
        (x) => x.field == this.fieldCase(name)
      );
      this.conf.columns.splice(index, 1);
      this.$emit("configChange", this.conf);
    },

    addStateColourProperty() {
      this.conf.columns.push({
        label: "",
        field: "",
        width: 30,
        numeric: false,
        html: false,
        color: true,
      });
      this.$emit("configChange", this.conf);
    },

    removeStateColourProperty() {
      var index = this.conf.columns.findIndex(
        (x) => x.label == "" && x.field == ""
      );
      if (index == -1) return;
      this.conf.columns.splice(index, 1);
      this.$emit("configChange", this.conf);
    },

    getStateTextByValue(val) {
      return this.$constants.AgentStates.find((x) => x.value == val).name;
    },

    updateOrder(cols) {
      this.conf.columns = cols;
      this.$emit("configChange", this.conf);
    },

    updateRowsPerPage(val) {
      this.conf.rows = val;
      this.$emit("configChange", this.conf);
    },

    endResize() {
      this.$store.commit("setTempLock", false);
      this.$emit("configChange", this.conf);
    },

    openMonitoring() {
      this.showMonitoring = true;
    },

    cancelMonitoring() {
      this.$diamond.cancelListener();
    },

    toggleMonitoring(ext) {
      this.showMonitoring = false;
      this.$state.commit("setExtension", ext);
    },

    isEmpty(obj) {
      return Object.keys(obj).length === 0;
    },

    addSpaces(str) {
      return str.replace(/([A-Z])/g, " $1").trim();
    },

    async kickAgent(agentName) {
      var confirm = await this.$refs.confirm.show({
        title: "Kick agent",
        message: `Are you sure you want to kick ${agentName}?`,
        okButton: "Kick",
        isConfirm: true,
      });

      if (confirm) {
        this.$diamond.kickAgent(agentName).then(() => {
          this.$toast(
            "success",
            `${agentName} has been kicked from the dialler.`
          );
        });
      }
    },

    updateSelected(row) {
      this.$store.commit("setAgent", row);
      if (
        this.monitoringState == this.$constants.MonitoringState.Connected ||
        this.monitoringState == this.$constants.MonitoringState.Monitoring
      ) {
        this.$diamond.listenToAgent(row.agentName);
      }
    },

    updateState(val) {
      if (val.detail.state == this.$constants.MonitoringAgentState.LoggedIn) {
        this.$store.commit(
          "setMonitoringState",
          this.$constants.MonitoringState.Connected
        );

        this.$diamond.listenToAgent(this.monitoringAgent.agentName).then(() => {
          this.$store.commit(
            "setMonitoringState",
            this.$constants.MonitoringState.Monitoring
          );
        });
      } else {
        this.$store.commit(
          "setMonitoringState",
          this.$constants.MonitoringState.NotConnected
        );
      }
    },

    addState(state) {
      this.conf.chosenStates.push({
        state: state,
        color: "#ffffff",
      });
      this.$emit("configChange", this.conf);
    },

    removeState(state) {
      var index = this.conf.chosenStates.findIndex((x) => x.state == state);
      this.conf.chosenStates.splice(index, 1);
      this.$emit("configChange", this.conf);
    },

    refreshTable() {
      this.$refs.table.refresh();
    },

    registerDependencies() {
      this.$store.getters.agentArgs.addProperty("AgentName");
      this.$store.getters.agentArgs.addProperty("AgentStateNum");
      this.$store.getters.agentArgs.addProperty("Campaign");
    },
  },
};
</script>

<style scoped>
.table {
  width: 100%;
  height: 100%;
}

.monitor-btn {
  box-shadow: 0 1px 3px rgb(0 0 0 / 7%);
  border-width: 1px;
  border-style: solid;
  border-color: #404a92;
  padding: 5px 18px 5px;
  color: #ffffff;
  font-weight: normal;
  font-size: 13px;
  line-height: normal;
  display: inline-block;
  cursor: pointer;
  text-align: center;
  vertical-align: middle;
  border-radius: 3px;
}

.gray {
  background-color: rgb(132, 142, 151);
}

.amber {
  background-color: rgb(51, 180, 212);
}

.green {
  background-color: rgb(189, 230, 127);
}

.btn-pos {
  position: absolute;
  bottom: 14px;
  left: 10px;
}
</style>
